import axios from 'axios';

const getApiEndpoint = () => {
  if (process.env.API_ENDPOINT) {
    return process.env.API_ENDPOINT;
  }
  const baseUrl = process.env.URL || '';
  return baseUrl.replace(/\/api\/v\d+$/i, '');
};

class Service {
  constructor() {
    const service = axios.create({
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });
    service.interceptors.request.use((config) => {
      const requestConfig = config || {};
      requestConfig.headers = requestConfig.headers || {};
      const tokenStorageKey = process.env.TOKEN;
      const token = tokenStorageKey ? localStorage.getItem(tokenStorageKey) : null;
      const projectStorageKey = process.env.PROJECT;
      const activeProjectId = projectStorageKey ? localStorage.getItem(projectStorageKey) : null;
      const shouldAttachProjectHeader = !requestConfig.skipProjectHeader;

      if (token) {
        requestConfig.headers.Authorization = `Bearer ${token}`;
      } else if (requestConfig.headers.Authorization) {
        delete requestConfig.headers.Authorization;
      }

      if (shouldAttachProjectHeader && activeProjectId) {
        requestConfig.headers['X-Project-Id'] = activeProjectId;
      } else if (requestConfig.headers['X-Project-Id']) {
        delete requestConfig.headers['X-Project-Id'];
      }

      if ('skipProjectHeader' in requestConfig) {
        delete requestConfig.skipProjectHeader;
      }

      return requestConfig;
    });
    service.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        return this.handleError(error);
      },
    );

    this.service = service;
    this.subscribers = [];
    this.isAlreadyFetchingAccessToken = false;
    this.isTokenExpiredError = true;
  }

  setHeader(name, value) {
    this.service.defaults.headers.common[name] = value;
  }

  removeHeader(name) {
    delete this.service.defaults.headers.common[name];
  }

  handleSuccess(response) {
    return response;
  }

  handleError = (error) => {
    if (!error?.response) {
      return Promise.reject(error?.message || error || 'Có lỗi kết nối mạng hoặc máy chủ không phản hồi');
    }

    switch (error.response.status) {
      case 401:
        return this.onLogout(error);
      default: {
        const data = error?.response?.data;
        const message = typeof data === 'object' && data !== null ? data?.message : null;

        if (data?.errors) {
          return Promise.reject(error);
        }

        if (message) {
          return Promise.reject(message);
        }

        if (typeof data === 'string' && data.trim().length > 0) {
          return Promise.reject(error.message || 'Có lỗi xảy ra từ máy chủ');
        }

        return Promise.reject(error?.message || error || 'Có lỗi xảy ra');
      }
    }
  };

  async resetTokenAndReattemptRequest(error) {
    try {
      const { response: errorResponse } = error;
      const retryOriginalRequest = new Promise((resolve) => {
        /* We need to add the request retry
        to the queue since there another request
        that already to refresh the token */
        this.addSubscriber((accessToken) => {
          errorResponse.config.headers.Authorization = `Bearer ${accessToken}`;
          return resolve(axios(errorResponse.config));
        });
      });
      if (!this.isAlreadyFetchingAccessToken) {
        this.isAlreadyFetchingAccessToken = true;
        const refreshToken = localStorage.getItem(process.env.REFRESH_TOKEN);
        try {
          const response = await axios({
            method: 'POST',
            url: `${getApiEndpoint()}/api/refresh-token`,
            data: {
              refreshToken,
            },
          });
          const newToken = response.data;
          localStorage.setItem(process.env.TOKEN, newToken);
          this.isAlreadyFetchingAccessToken = false;
          this.service.defaults.headers.common.Authorization = `Bearer ${newToken}`;
          this.onAccessTokenFetched(newToken);
        } catch (err) {
          const { status } = err.response;
          // if (status === 401) {
          localStorage.removeItem(process.env.TOKEN);
          localStorage.removeItem(process.env.REFRESH_TOKEN);
          this.redirectTo(document, '/login');
          // }
          return Promise.reject(err);
        }
      }
      return retryOriginalRequest;
    } catch (err) {
      return Promise.reject(err);
    }
  }

  onLogout() {
    localStorage.removeItem(process.env.TOKEN);
    this.redirectTo(document, '/login');
  }

  onAccessTokenFetched(accessToken) {
    this.subscribers.forEach((callback) => callback(accessToken));
    this.subscribers = [];
  }

  addSubscriber(callback) {
    this.subscribers.push(callback);
  }

  redirectTo = (document, path) => {
    if (document.location.pathname === path) return;
    document.location = path;
  };

  get(endpoint, params, options = {}) {
    const requestConfig = {
      ...options,
      params: {
        ...(options.params || {}),
        ...(params || {}),
      },
    };

    return this.service.get(endpoint, requestConfig);
  }

  post(endpoint, payload, options = {}) {
    return this.service.request({
      method: 'POST',
      url: endpoint,
      responseType: 'json',
      data: payload,
      ...options,
    });
  }

  patch(endpoint, payload, options = {}) {
    return this.service.request({
      ...options,
      method: 'PATCH',
      url: endpoint,
      responseType: 'json',
      data: payload,
    });
  }

  put(endpoint, payload, options = {}) {
    return this.service.request({
      ...options,
      method: 'PUT',
      url: endpoint,
      responseType: 'json',
      data: payload,
    });
  }

  delete(endpoint, payload, options = {}) {
    return this.service.request({
      ...options,
      method: 'DELETE',
      url: endpoint,
      responseType: 'json',
      data: payload,
    });
  }
}
const service = new Service();

export default service;
